const ARABIC_DIGITS = /[\u0660-\u0669\u06F0-\u06F9]/g;
const SKIPPED_ELEMENT_SELECTOR = [
  'script',
  'style',
  'textarea',
  'input',
  '[contenteditable]',
  'code',
  'pre',
  'kbd',
  'samp'
].join(',');

/**
 * Presentation-only digit normalization. Stored values and route/API data stay
 * untouched; only strings passed to this helper are converted.
 */
export function toAsciiDigits(value) {
  if (typeof value !== 'string') return value;
  return value.replace(ARABIC_DIGITS, digit => {
    const code = digit.charCodeAt(0);
    return String(code - (code <= 0x0669 ? 0x0660 : 0x06F0));
  });
}

function isSkipped(node) {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(element?.closest(SKIPPED_ELEMENT_SELECTOR));
}

function collectTextNodes(node, textNodes) {
  if (node.nodeType === Node.TEXT_NODE) {
    textNodes.add(node);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE || isSkipped(node)) return;

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
    acceptNode(textNode) {
      return isSkipped(textNode)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    }
  });
  let textNode;
  while ((textNode = walker.nextNode())) textNodes.add(textNode);
}

function normalizeTextNode(node) {
  if (!node.isConnected || isSkipped(node)) return;
  const normalized = toAsciiDigits(node.nodeValue);
  if (normalized !== node.nodeValue) node.nodeValue = normalized;
}

function normalizeDisplayAttributes(element) {
  if (!element.isConnected || isSkipped(element)) return;
  for (const name of ['title', 'aria-label']) {
    if (!element.hasAttribute(name)) continue;
    const value = element.getAttribute(name);
    const normalized = toAsciiDigits(value);
    if (normalized !== value) element.setAttribute(name, normalized);
  }
}

/**
 * Keeps all rendered portal text on ASCII digits without replacing DOM nodes.
 * Mutation records are handled as a batch and only changed/new subtrees are
 * visited; subsequent React updates therefore keep their normal reconciliation.
 */
export function installAsciiDigitPresentation(root = document.documentElement) {
  if (!root) return () => {};

  const initialTextNodes = new Set();
  collectTextNodes(root, initialTextNodes);
  initialTextNodes.forEach(normalizeTextNode);
  if (root.nodeType === Node.ELEMENT_NODE) {
    normalizeDisplayAttributes(root);
    root.querySelectorAll('[title], [aria-label]').forEach(normalizeDisplayAttributes);
  }

  const observer = new MutationObserver(records => {
    const textNodes = new Set();
    const attributeElements = new Set();

    for (const record of records) {
      if (record.type === 'characterData') {
        textNodes.add(record.target);
      } else if (record.type === 'childList') {
        record.addedNodes.forEach(node => collectTextNodes(node, textNodes));
        record.addedNodes.forEach(node => {
          if (node.nodeType !== Node.ELEMENT_NODE || isSkipped(node)) return;
          if (node.matches('[title], [aria-label]')) attributeElements.add(node);
          node.querySelectorAll('[title], [aria-label]').forEach(element => {
            if (!isSkipped(element)) attributeElements.add(element);
          });
        });
      } else if (record.type === 'attributes') {
        attributeElements.add(record.target);
      }
    }

    textNodes.forEach(normalizeTextNode);
    attributeElements.forEach(normalizeDisplayAttributes);
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['title', 'aria-label']
  });
  return () => observer.disconnect();
}